import BiSubstack from '~icons/bi/substack';
import TablerIconBrandBluesky from '~icons/tabler/brand-bluesky';
import TablerIconBrandDiscord from '~icons/tabler/brand-discord';
import TablerIconBrandGitHub from '~icons/tabler/brand-github';
import TablerIconBrandLinkedin from '~icons/tabler/brand-linkedin';
import TablerIconBrandX from '~icons/tabler/brand-x';

import { Container, Stack, Typography } from 'gooey';
import { Glow } from '#/components/design/glow';
import { Img } from '#/components/images';
import { Link, type RouterLinkTo } from '#/components/links';
import type { Layout } from '#/content';
import { cn } from '#/utils/classnames';

export type FooterProps = Layout['footer'];

export function Footer({ nav, social }: FooterProps) {
  const navLegal = nav.find((group) => group.title === 'Legal');
  return (
    <footer className='border-solid border-t border-gray-600 relative overflow-hidden footer sm:footer-horizontal'>
      <Glow className='z-0 top-0 right-0 left-0 max-w-[1200px] m-auto' />
      <Container className='pt-24 pb-24'>
        {/* <iframe
          title='Substack'
          src="https://duckiedocs.substack.com/embed"
          width="480"
          height="150"
          style={{ border: 'none', background: 'transparent' }}
          frameBorder="0"
          scrolling="no"
        /> */}
        <Stack direction='row' className='w-full flex-wrap md:flex-nowrap'>
          <Stack
            spacing={1}
            alignItems={{ xs: 'center', sm: 'center', md: 'start' }}
            className='w-full md:w-1/2 mx-auto mb-4'
          >
            <Address />
            <BackedByTechstars />
            <SocialIcons socials={social} />
            <StatusPage />
          </Stack>
          <Stack
            className='flex-wrap md:flex-nowrap max-sm:my-8'
            direction='row'
            spacing={{ xs: 4, sm: 4, md: 8 }}
            alignItems={{ xs: 'start', sm: 'start', md: 'stretch' }}
            justifyContent={{ xs: 'center', sm: 'center', md: 'space-between' }}
          >
            {nav.map((group) => (
              <LinkGroup
                key={group.title}
                title={group.title}
                links={group.links.map((link) => ({
                  text: link.title,
                  url: link.url,
                }))}
                className='w-fit md:w-full'
              />
            ))}
          </Stack>
        </Stack>
        <div className='text-center w-full mt-8 md:mt-24'>
          <Copyright />
        </div>
        <Stack
          className='flex sm:hidden w-full mt-8'
          direction='row'
          alignContent='center'
          justifyContent='center'
          alignItems='center'
          spacing={1}
          divider={<span>•</span>}
        >
          {navLegal?.links.map(({ title, url }) => (
            <Link
              key={title}
              to={url as RouterLinkTo}
              className='data-[status=active]:text-gray-500'
            >
              {title}
            </Link>
          ))}
        </Stack>
      </Container>
    </footer>
  );
}

/*
|------------------
| Internal
|------------------
*/

function LinkGroup(props: {
  title: string;
  links: { text: string; url: string }[];
  className?: string;
}) {
  return (
    <Stack
      component='nav'
      direction='column'
      spacing={3}
      className={cn('text-gray-300 text-sm sm:text-base md:text-lg', props.className)}
    >
      <Typography
        variant='h5'
        component='h6'
        className='text-md sm:text-lg md:text-xl font-semibold'
      >
        {props.title}
      </Typography>
      <Stack spacing={1}>
        {props.links.map(({ text, url }) => {
          if (url.startsWith('http')) {
            return (
              <Link
                key={text}
                href={url}
                openInNewTab
                className='data-[status=active]:text-gray-500'
              >
                {text}
              </Link>
            );
          }

          return (
            <Link
              key={text}
              to={url as RouterLinkTo}
              className='data-[status=active]:text-gray-500'
            >
              {text}
            </Link>
          );
        })}
      </Stack>
    </Stack>
  );
}

function SocialIcons(props: {
  socials: { icon: string; url: string }[];
}) {
  return (
    <Stack direction='row' spacing={1} alignItems='center' className='w-fit'>
      {props.socials.map(({ icon, url }) => (
        <a
          key={icon}
          color='inherit'
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          className='p-2 rounded-xl hover:bg-gray-900'
        >
          {getIcon(icon as Icon)}
        </a>
      ))}
    </Stack>
  );
}

function StatusPage() {
  return (
    <div className='text-center'>
      <iframe
        title="Joggr's status page"
        src='https://status.joggr.io/badge?theme=dark'
        style={{
          colorScheme: 'none',
        }}
        width='200'
        height='30'
        frameBorder='0'
        scrolling='no'
        // @ts-expect-error
        align='right'
      />
    </div>
  );
}

function Address() {
  return (
    <address className='text-gray-300 text-sm sm:text-base md:text-base text-center md:text-left w-fit'>
      1216 Broadway, Suite 421 <br /> New York, NY 10001
    </address>
  );
}

function Copyright() {
  return (
    <Typography variant='body2' className='text-gray-300 text-sm sm:text-base md:text-lg'>
      {'Copyright © '}
      &nbsp;
      {new Date().getFullYear()}
      &nbsp;
      {'Joggr, Inc. All rights reserved.'}
    </Typography>
  );
}

function BackedByTechstars() {
  return (
    <Stack direction='row' spacing={1} alignItems='center' className='w-fit'>
      <Typography variant='body2'>Backed by</Typography>
      <Img
        src='techstars.png'
        alt='Techstars logo'
        height={40}
        width={120}
        className='h-[14px] w-auto'
        responsive
      />
    </Stack>
  );
}

type Icon = 'github' | 'linkedin' | 'discord' | 'bluesky' | 'x' | 'twitter' | 'substack';

function getIcon(icon: Icon) {
  switch (icon) {
    case 'github':
      return <TablerIconBrandGitHub />;
    case 'linkedin':
      return <TablerIconBrandLinkedin />;
    case 'discord':
      return <TablerIconBrandDiscord />;
    case 'bluesky':
      return <TablerIconBrandBluesky />;
    case 'substack':
      return <BiSubstack />;
    case 'x':
    case 'twitter':
      return <TablerIconBrandX />;
  }
}
